/**
 * One rule (knockout or scoring): question, operator, a value input typed by
 * the question's answer type, then the rule's own field (reason or points).
 */
import { useId, type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { RULE_OPS, type RuleOp, type RuleValue, type ScreeningQuestion } from './types';
import { opNeedsValue, type RuleErrors } from './validate';

export const ruleInput =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 aria-[invalid=true]:border-danger';

export function RuleErr({ id, text }: { id: string; text: string | undefined }) {
  if (!text) return null;
  return <span id={id} role="alert" className="mt-1 block text-[12px] text-danger">{text}</span>;
}

function valueText(v: RuleValue): string {
  if (v === null) return '';
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}

/** The value input depends on the op and the question's type. */
function ValueInput({
  id, op, question, value, invalid, describedBy, onChange,
}: {
  id: string; op: RuleOp; question: ScreeningQuestion | undefined; value: RuleValue; invalid: boolean; describedBy: string | undefined; onChange: (v: RuleValue) => void;
}) {
  const common = { id, 'aria-invalid': invalid || undefined, 'aria-describedby': describedBy, className: ruleInput };
  if (op === 'in') {
    return <input {...common} value={valueText(value)} placeholder="Comma-separated, e.g. Days, Nights" onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />;
  }
  if (question?.type === 'boolean' && (op === 'eq' || op === 'neq')) {
    return (
      <select {...common} value={value === true ? 'true' : value === false ? 'false' : ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}>
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (question?.type === 'choice' && (op === 'eq' || op === 'neq') && (question.choices ?? []).length > 0) {
    return (
      <select {...common} value={valueText(value)} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">Choose…</option>
        {(question.choices ?? []).filter((c) => c.trim()).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  const numeric = question?.type === 'number' || ['gt', 'gte', 'lt', 'lte'].includes(op);
  return (
    <input
      {...common}
      type={numeric ? 'number' : 'text'}
      inputMode={numeric ? 'decimal' : undefined}
      step={numeric ? 'any' : undefined}
      value={valueText(value)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') return onChange(null);
        const n = Number(raw);
        onChange(numeric && Number.isFinite(n) ? n : raw);
      }}
    />
  );
}

export function RuleRow({
  label, index, questions, field, op, value, errors, onChange, onRemove, children,
}: {
  label: string; index: number; questions: ScreeningQuestion[]; field: string; op: RuleOp; value: RuleValue;
  errors: RuleErrors | undefined; onChange: (patch: { field?: string; op?: RuleOp; value?: RuleValue }) => void; onRemove: () => void;
  /** The rule-specific trailing field (reason or points). */
  children: (ids: { id: string }) => ReactNode;
}) {
  const id = useId();
  const question = questions.find((q) => q.key === field);
  const needsValue = opNeedsValue(op);
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3" aria-label={`${label} ${index + 1}`} data-testid={`${label.toLowerCase()}-${index}`}>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="text-[13px]">
          <label htmlFor={`${id}-field`} className="font-medium text-gray-800">Question</label>
          <select id={`${id}-field`} value={field} onChange={(e) => onChange({ field: e.target.value, value: null })} aria-invalid={errors?.field ? true : undefined} aria-describedby={errors?.field ? `${id}-field-err` : undefined} className={ruleInput}>
            <option value="">Choose…</option>
            {questions.filter((q) => q.key).map((q) => <option key={q.key} value={q.key}>{q.prompt.trim() || q.key}</option>)}
          </select>
          <RuleErr id={`${id}-field-err`} text={errors?.field} />
        </div>
        <div className="text-[13px]">
          <label htmlFor={`${id}-op`} className="font-medium text-gray-800">Condition</label>
          <select id={`${id}-op`} value={op} onChange={(e) => onChange({ op: e.target.value as RuleOp, value: opNeedsValue(e.target.value as RuleOp) ? value : null })} className={ruleInput}>
            {RULE_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {needsValue && (
          <div className="text-[13px]">
            <label htmlFor={`${id}-value`} className="font-medium text-gray-800">Value</label>
            <ValueInput id={`${id}-value`} op={op} question={question} value={value} invalid={!!errors?.value} describedBy={errors?.value ? `${id}-value-err` : undefined} onChange={(v) => onChange({ value: v })} />
            <RuleErr id={`${id}-value-err`} text={errors?.value} />
          </div>
        )}
      </div>
      <div className="mt-2 flex items-end gap-2">
        <div className="min-w-0 flex-1">{children({ id })}</div>
        <button type="button" onClick={onRemove} aria-label={`Remove ${label.toLowerCase()} ${index + 1}`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-danger hover:bg-danger-bg">
          <Trash2 size={15} strokeWidth={2} aria-hidden />
        </button>
      </div>
    </li>
  );
}
